import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import {
  ContactsDetailsDealsPageDocument,
  DealsPipelineViewDocument,
  FinanceValueObjectsContactTypeEnum,
  FinanceValueObjectsDealStageEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { DealCreateModal } from 'modules/finance/components/Deals/CreateModal'
import { DealsPipelineView } from 'modules/finance/components/Deals/DealsPipelineView'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

interface ContactsDetailsDealsPageProps {
  contactType: FinanceValueObjectsContactTypeEnum
}

const ContactsDetailsDealsPage: React.FC<ContactsDetailsDealsPageProps> = ({ contactType }) => {
  const apolloClient = useApolloClient()
  const { contactId } = useParams()
  const {
    company,
    access: { locale },
    canRequestUpsell,
  } = useCurrentSession()
  const [stage, setStage] = useState<FinanceValueObjectsDealStageEnum>()
  const { data, loading, error } = useQuery(ContactsDetailsDealsPageDocument, {
    variables: {
      companyId: company.id,
      id: contactId ?? '0',
    },
  })

  const contact = data?.finance?.contact ?? null
  const hasDealsFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.sales_deals
  )?.available

  const canSeeDealsContent =
    data?.finance?._policies?.sidebarSeeFinanceDealsSubLevelItem?.can ?? false
  const canSeeDealsUpsell = !hasDealsFeature && canRequestUpsell
  const canSeePage = contactType === 'client' && (canSeeDealsContent || canSeeDealsUpsell)

  useEffect(() => {
    track(trackingEvents.accounting.deals.viewClientDealsPage)
  }, [])

  useEffect(() => {
    track(trackingEvents.accounting.viewContactDetailsPage, {
      view: 'deals',
      deals_upsell: canSeeDealsUpsell,
    })
  }, [canSeeDealsUpsell])

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const handleDealCreated = useCallback(() => {
    void apolloClient.refetchQueries({ include: [DealsPipelineViewDocument] })
    track(trackingEvents.accounting.deals.submitCreate, {
      source: 'sales_contact_details_deals_page',
    })
  }, [apolloClient])

  const handleOpenModal = (stage?: FinanceValueObjectsDealStageEnum) => {
    setStage(stage)
    setIsCreateModalOpen(true)
    track(trackingEvents.accounting.deals.startCreate, {
      source: 'sales_contact_details_deals_page',
    })
  }

  const handleCloseModal = () => {
    setIsCreateModalOpen(false)
  }

  const handleChangeStage = (dealId: string, newStage: FinanceValueObjectsDealStageEnum) => {
    track(trackingEvents.accounting.deals.changeStage, {
      source: 'sales_contact_details_deals_page',
      dealId,
      newStage,
    })
  }

  return (
    <ResponsivePage.Body authorized={canSeePage} loading={loading} error={error}>
      {!hasDealsFeature ? (
        <Box alignItems="center" justifyContent="center" style={{ height: '50vh' }}>
          <UpsellingKit.EmptyState
            moduleRequested={AvailableModules.sales}
            featureRequested={Features.sales_deals}
            requestReason={ValidReasons.MissingFeature}
            requestSource="sales_from_contact_details_on_deals_tab"
            title={i18n.t('finance.contacts.contact_details.deals.upsell_title')}
            description={i18n.t('finance.contacts.contact_details.deals.upsell_description')}
          />
        </Box>
      ) : (
        <>
          <DealsPipelineView
            locale={locale}
            onClickCreateDeal={handleOpenModal}
            onChangeStage={handleChangeStage}
            clientId={contactId}
            showClientColumn={false}
          />

          <DealCreateModal
            isOpen={isCreateModalOpen}
            onClose={handleCloseModal}
            refetchQuery="DealsPipelineView"
            defaultClient={contact}
            stage={stage}
            onSuccess={handleDealCreated}
          />
        </>
      )}
    </ResponsivePage.Body>
  )
}

export default ContactsDetailsDealsPage
